import { useCallback, useEffect, useMemo } from 'react'
import { FiCopy, FiX } from 'react-icons/fi'
import {
  EmailIcon,
  EmailShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  RedditIcon,
  RedditShareButton,
  TelegramIcon,
  TelegramShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
  XShareButton,
} from 'react-share'
import { toast } from 'sonner'

interface NewsShareModalProps {
  isOpen: boolean
  onClose: () => void
  shareUrl: string
  title: string
  description: string
  imageUrl?: string
}

export default function NewsShareModal({
  isOpen,
  onClose,
  shareUrl,
  title,
  description,
  imageUrl,
}: NewsShareModalProps) {
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => {
      window.removeEventListener('keydown', handleEscape)
    }
  }, [handleClose, isOpen])

  const emailBody = useMemo(() => {
    if (!description.trim()) {
      return shareUrl
    }
    return `${description}\n\n${shareUrl}`
  }, [description, shareUrl])

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link copied.')
    } catch {
      toast.error('Could not copy link.')
    }
  }

  if (!isOpen) {
    return null
  }

  const shareButtonClassName =
    'inline-flex h-13 w-full appearance-none items-center gap-3 rounded-xl border border-border-light bg-surface px-4 py-3 text-left text-sm font-bold text-text-primary transition-colors hover:bg-border-light/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/40'
  const copyButtonClassName =
    'inline-flex h-13 w-full appearance-none items-center justify-start gap-3 border-0 bg-transparent p-0 text-left text-sm font-normal text-text-primary transition-opacity hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/40'

  return (
    <div className="fixed inset-0 z-60 bg-black/55">
      <button
        type="button"
        className="absolute inset-0"
        aria-label="Close share dialog"
        onClick={handleClose}
      />
      <div className="relative z-10 flex h-full w-full items-center justify-center p-4 md:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-label="Share this article"
          className="relative w-full max-w-3xl max-h-[calc(100vh-2rem)] overflow-y-auto rounded-4xl bg-background p-6 shadow-2xl sm:max-h-[calc(100vh-3rem)] sm:p-8"
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close share dialog"
            className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-background"
          >
            <FiX className="h-6 w-6" />
          </button>

          <h3 className="pr-10 text-2xl font-bold text-text-primary sm:text-3xl">
            Share this article
          </h3>
          <p className="mt-2 text-sm text-text-secondary sm:text-base">
            Share via social networks, email, or copy the article link.
          </p>

          {imageUrl ? (
            <div className="mt-5 overflow-hidden rounded-2xl border border-border-light bg-background">
              <img
                src={imageUrl}
                alt={title}
                className="h-36 w-full object-cover sm:h-44"
              />
            </div>
          ) : null}

          <p className="mt-4 line-clamp-2 text-base font-bold text-text-primary sm:text-lg">
            {title}
          </p>
          <div className="mt-4 rounded-xl border-2 border-border-light bg-surface px-4 py-3">
            <div className="flex items-center gap-3">
              <p className="line-clamp-2 min-w-0 flex-1 break-all text-center text-sm font-semibold text-text-secondary sm:text-base">
                {shareUrl}
              </p>
              <button
                type="button"
                onClick={() => {
                  void copyLink()
                }}
                title="Copy article link"
                aria-label="Copy article link"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue/40"
              >
                <FiCopy className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <EmailShareButton
              url={shareUrl}
              subject={title}
              body={emailBody}
              className={shareButtonClassName}
              htmlTitle="Share by email"
            >
              <EmailIcon size={42} round />
              <span>Email</span>
            </EmailShareButton>

            <LinkedinShareButton
              url={shareUrl}
              title={title}
              summary={description}
              source="Parkinson's Insights"
              className={shareButtonClassName}
              htmlTitle="Share on LinkedIn"
            >
              <LinkedinIcon size={42} round />
              <span>LinkedIn</span>
            </LinkedinShareButton>

            <RedditShareButton
              url={shareUrl}
              title={title}
              className={shareButtonClassName}
              htmlTitle="Share on Reddit"
            >
              <RedditIcon size={42} round />
              <span>Reddit</span>
            </RedditShareButton>

            <TelegramShareButton
              url={shareUrl}
              title={title}
              className={shareButtonClassName}
              htmlTitle="Share on Telegram"
            >
              <TelegramIcon size={42} round />
              <span>Telegram</span>
            </TelegramShareButton>

            <WhatsappShareButton
              url={shareUrl}
              title={title}
              separator=" "
              className={shareButtonClassName}
              htmlTitle="Share on WhatsApp"
            >
              <WhatsappIcon size={42} round />
              <span>WhatsApp</span>
            </WhatsappShareButton>

            <XShareButton
              url={shareUrl}
              title={title}
              className={shareButtonClassName}
              htmlTitle="Share on X"
            >
              <XIcon size={42} round />
              <span>X</span>
            </XShareButton>

            <button
              type="button"
              onClick={() => {
                void copyLink()
              }}
              className={copyButtonClassName}
              title="Copy article link"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue text-white">
                <FiCopy className="h-6 w-6" />
              </span>
              <span>Copy link</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  )
}
